import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
// @ts-ignore
import { getHeroForCustomer } from "../../services/heroService";
// @ts-ignore
import { getAboutPageForCustomer } from "../../services/aboutPageService";
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';
import chuyenDoiSoImage from '../../assets/images/chuyen-doi-so.jpg';
import stackwayTeamImage from '../../assets/images/Stackway-Team.jpg';
import stackwayAcademyImage from '../../assets/images/stackway-academy.jpg';
import congNgheTienPhongImage from '../../assets/images/cong-nghe-tien-phong.jpg';
import nhanDienThuongHieu from '../../assets/images/nhandienthuonghieu.jpg';

interface HeroSection {
  id: string;
  page: string;
  badge?: {
    text: string;
    icon: string;
  };
  heading: string;
  subheading?: string;
  description?: string;
  backgroundImage?: string;
  buttons: Array<{
    text: string;
    url: string;
    actionType: 'modal' | 'url' | 'scroll';
    style: 'primary' | 'secondary' | 'outline';
    order: number;
    isVisible: boolean;
  }>;
  isActive: boolean;
}

interface AboutPageData {
  story?: {
    image: string;
    title: string;
    paragraphs: string[];
    quote: string;
  };
  mission?: { title: string; content: string };
  vision?: { title: string; content: string };
  values?: {
    heading: string;
    subheading: string;
    items: Array<{ icon: string; title: string; description: string; gradient: string }>;
  };
  brandIdentity?: {
    badge: string;
    slogan: string;
    logoMeaning: string[];
    image: string;
  };
  services?: {
    heading: string;
    subheading: string;
    items: Array<{ image: string; icon: string; title: string; description: string }>;
  };
}

const DEFAULT_VALUES = {
  items: [
    { icon: "ri-lightbulb-flash-line", title: "Sáng tạo & Đổi mới", description: "Luôn tìm kiếm và áp dụng công nghệ mới, không ngừng đổi mới để mang đến giải pháp tốt nhất.", gradient: "from-[#00c897] to-[#00b587]" },
    { icon: "ri-heart-line", title: "Trách nhiệm xã hội", description: "Cam kết phát triển bền vững, đóng góp tích cực cho cộng đồng và xã hội.", gradient: "from-[#2a4b7c] to-[#1e3a5f]" },
    { icon: "ri-rocket-line", title: "Năng động & Trẻ trung", description: "Đội ngũ trẻ, nhiệt huyết, luôn sẵn sàng đối mặt với thách thức và học hỏi không ngừng.", gradient: "from-[#00c897] to-[#00b587]" },
    { icon: "ri-shield-check-line", title: "Chất lượng & Uy tín", description: "Đặt chất lượng lên hàng đầu, xây dựng niềm tin với khách hàng qua từng sản phẩm.", gradient: "from-[#2a4b7c] to-[#1e3a5f]" },
    { icon: "ri-team-line", title: "Tận tâm & Đồng hành", description: "Không chỉ là nhà cung cấp mà còn là đối tác đồng hành tin cậy trong mọi dự án.", gradient: "from-[#00c897] to-[#00b587]" },
    { icon: "ri-book-open-line", title: "Học hỏi & Phát triển", description: "Khuyến khích văn hóa học tập, phát triển kỹ năng và chia sẻ kiến thức trong đội ngũ.", gradient: "from-[#2a4b7c] to-[#1e3a5f]" },
  ],
};

export default function AboutPage() {
  const [visibleSections, setVisibleSections] = useState({
    story: false,
    mission: false,
    values: false,
    brandIdentity: false,
    services: false,
    team: false,
    cta: false,
  });
  const [heroSection, setHeroSection] = useState<HeroSection | null>(null);
  const [aboutData, setAboutData] = useState<AboutPageData | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useAutoSEO();

  const storyRef = useRef<HTMLElement>(null);
  const missionRef = useRef<HTMLElement>(null);
  const valuesRef = useRef<HTMLElement>(null);
  const brandIdentityRef = useRef<HTMLElement>(null);
  const servicesRef = useRef<HTMLElement>(null);
  const teamRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        const heroResponse = await getHeroForCustomer('about');
        if (heroResponse.success && heroResponse.data) {
          setHeroSection({
            ...heroResponse.data,
            isActive: heroResponse.data.isActive !== undefined ? heroResponse.data.isActive : true,
            buttons: heroResponse.data.buttons || []
          });
        }
      } catch (error) {
        console.error('Error fetching hero data:', error);
      }
    };
    fetchHeroData();
  }, []);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const res = await getAboutPageForCustomer();
        if (res.success && res.data) setAboutData(res.data);
      } catch (error) {
        console.error('Error fetching about page data:', error);
      }
    };
    fetchAboutData();
  }, []);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  useEffect(() => {
    const observerOptions = { threshold: 0.2, rootMargin: "0px 0px -100px 0px" };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) setVisibleSections((prev) => ({ ...prev, [sectionName]: true }));
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const refs = [
      { ref: storyRef, name: "story" },
      { ref: missionRef, name: "mission" },
      { ref: valuesRef, name: "values" },
      { ref: brandIdentityRef, name: "brandIdentity" },
      { ref: servicesRef, name: "services" },
      { ref: teamRef, name: "team" },
      { ref: ctaRef, name: "cta" },
    ];
    refs.forEach(({ ref }) => ref.current && observer.observe(ref.current));
    return () => refs.forEach(({ ref }) => ref.current && observer.unobserve(ref.current));
  }, []);

  const story = aboutData?.story;
  const mission = aboutData?.mission;
  const vision = aboutData?.vision;
  const values = aboutData?.values;
  const brandIdentity = aboutData?.brandIdentity;
  const services = aboutData?.services;

  const coreValues = (values?.items && values.items.length > 0) ? values.items : DEFAULT_VALUES.items;
  const servicesItems = services?.items || [];
  const serviceImages = [chuyenDoiSoImage, congNgheTienPhongImage, stackwayAcademyImage];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className={`text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {heroSection?.badge && (
              <div className="inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                {heroSection.badge.icon && <i className={`${heroSection.badge.icon} mr-2`}></i>}
                <span className="text-[#00c897] text-sm font-semibold">{heroSection.badge.text}</span>
              </div>
            )}
            {!heroSection?.badge && (
              <div className="inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm">
                <span className="text-[#00c897] text-sm font-semibold">Startup phần mềm trẻ trung, năng động</span>
              </div>
            )}
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              {heroSection?.heading || "Về Stackway"}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {heroSection?.description || heroSection?.subheading || "Mang sức trẻ và sự sáng tạo phục vụ xã hội - Xây dựng giải pháp công nghệ chất lượng cao cho kỷ nguyên số"}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <svg className="w-full h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z" fill="white"></path>
          </svg>
        </div>
      </section>

      {/* Story Section */}
      <section ref={storyRef} data-section="story" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-1000 ${visibleSections.story ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={story?.image || stackwayTeamImage}
                  alt="Stackway Team"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/50 to-transparent"></div>
              </div>
            </div>
            <div className={`transition-all duration-1000 delay-200 ${visibleSections.story ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
              <h2 className="text-4xl font-bold text-[#2a4b7c] mb-6">
                {story?.title || "Câu Chuyện Stackway"}
              </h2>
              <div className="space-y-4 text-[#4b5563] leading-relaxed prose prose-p:text-[#4b5563] prose-strong:text-[#2a4b7c] max-w-none">
                {story?.paragraphs?.length ? (
                  story.paragraphs.map((p, i) => (
                    <div key={i} dangerouslySetInnerHTML={{ __html: p }} />
                  ))
                ) : (
                  <>
                    <p>Stackway là một startup phần mềm trẻ trung và năng động, được thành lập với sứ mệnh mang sức trẻ, sự sáng tạo và tinh thần trách nhiệm để phục vụ xã hội. Chúng tôi đang phát triển nhanh chóng, mạnh mẽ, tập trung vào việc xây dựng các giải pháp công nghệ chất lượng cao, giúp các tổ chức và doanh nghiệp vượt qua thách thức trong kỷ nguyên số.</p>
                    <p>Với đội ngũ gồm những chuyên gia trẻ tuổi, đầy nhiệt huyết và kinh nghiệm thực tiễn, Stackway không chỉ là một công ty phần mềm mà còn là người bạn đồng hành đáng tin cậy trong hành trình chuyển đổi số.</p>
                  </>
                )}
                {story?.quote && (
                  <div className="bg-gradient-to-r from-[#00c897]/10 to-transparent border-l-4 border-[#00c897] p-6 rounded-r-lg">
                    <div className="flex items-start gap-3">
                      <i className="ri-quote-text text-3xl text-[#00c897] flex-shrink-0"></i>
                      <div className="text-[#2a4b7c] font-semibold italic prose prose-p:my-0 max-w-none" dangerouslySetInnerHTML={{ __html: story.quote }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section ref={missionRef} data-section="mission" className="py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8">
            <div className={`bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 ${visibleSections.mission ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-2xl mb-6 shadow-lg">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">{mission?.title || "Sứ Mệnh"}</h3>
              <div className="text-gray-200 leading-relaxed prose prose-p:text-gray-200 prose-p:my-2 max-w-none" dangerouslySetInnerHTML={{ __html: mission?.content || "Sứ mệnh của Stackway là mang sức trẻ và sự sáng tạo để phát triển các giải pháp công nghệ tiên tiến, thúc đẩy chuyển đổi số cho doanh nghiệp, tổ chức chính phủ và xã hội." }} />
            </div>
            <div className={`bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 delay-200 ${visibleSections.mission ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-2xl mb-6 shadow-lg">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">{vision?.title || "Tầm Nhìn"}</h3>
              <div className="text-gray-200 leading-relaxed prose prose-p:text-gray-200 prose-p:my-2 max-w-none" dangerouslySetInnerHTML={{ __html: vision?.content || "StackWay hướng tới trở thành một trong những công ty công nghệ uy tín tại Việt Nam, tiên phong trong xây dựng các giải pháp chuyển đổi số bền vững." }} />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section ref={valuesRef} data-section="values" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleSections.values ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-4xl font-bold text-[#2a4b7c] mb-4">
              {values?.heading || "Giá Trị Cốt Lõi"}
            </h2>
            <p className="text-lg text-[#4b5563] max-w-2xl mx-auto">
              {values?.subheading || "Những giá trị định hướng mọi hành động và quyết định của chúng tôi"}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className={`group bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-[#00c897] transition-all duration-500 hover:shadow-xl cursor-pointer ${visibleSections.values ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${value.gradient} rounded-xl mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#2a4b7c] mb-3 group-hover:text-[#00c897] transition-colors">{value.title}</h3>
                <div className="text-[#4b5563] leading-relaxed prose prose-p:text-[#4b5563] prose-p:my-0 max-w-none" dangerouslySetInnerHTML={{ __html: value.description || "" }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Identity */}
      <section ref={brandIdentityRef} data-section="brandIdentity" className="py-24" style={{ backgroundColor: '#f6f7f8' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-all duration-1000 ${visibleSections.brandIdentity ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-[#00c897]/10 text-[#00c897] text-sm font-medium mb-6">
                <i className="ri-palette-line"></i>
                <span>{brandIdentity?.badge || "NHẬN DIỆN THƯƠNG HIỆU STACKWAY"}</span>
              </div>
              <div className="space-y-6 mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-[#2a4b7c] mb-3">Slogan</h3>
                  <div className="text-3xl font-bold text-[#2a4b7c] italic leading-tight prose prose-p:my-0 max-w-none" dangerouslySetInnerHTML={{ __html: brandIdentity?.slogan || '<span class="text-[#2a4b7c]">StackWay</span> – Con đường xây dựng <span class="text-[#06b6d4] font-semibold">nền tảng số vững chắc</span>' }} />
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-[#2a4b7c] mb-4">Ý nghĩa logo</h3>
                  {brandIdentity?.logoMeaning?.length ? (
                    <div className="space-y-4 text-[#4b5563] leading-relaxed text-justify prose prose-p:text-[#4b5563] prose-strong:text-[#2a4b7c] max-w-none">
                      {brandIdentity.logoMeaning.map((p, i) => (
                        <div key={i} dangerouslySetInnerHTML={{ __html: p }} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#4b5563] leading-relaxed text-justify">
                      Logo <span className="text-[#2a4b7c] font-semibold">StackWay</span> được thiết kế dựa trên hình khối lập phương 3D, đại diện cho <span className="text-[#06b6d4] font-semibold">nền tảng công nghệ cốt lõi</span> và <span className="text-[#06b6d4] font-semibold">kiến trúc hệ thống nhiều lớp (stack)</span>.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className={`transition-all duration-1000 delay-200 flex justify-center ${visibleSections.brandIdentity ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
              <div className="relative max-w-md w-full">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl group" style={{ borderRadius: '24px' }}>
                  <img
                    src={brandIdentity?.image || nhanDienThuongHieu}
                    alt="Nhận diện thương hiệu StackWay"
                    className="w-full h-auto object-cover group-hover:scale-110 transition-transform duration-500"
                    style={{ borderRadius: '24px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ borderRadius: '24px' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section ref={servicesRef} data-section="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleSections.services ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-4xl font-bold text-[#2a4b7c] mb-4">
              {services?.heading || "Ba Lĩnh Vực Cốt Lõi"}
            </h2>
            <p className="text-lg text-[#4b5563] max-w-2xl mx-auto">
              {services?.subheading || "Chúng tôi tập trung phát triển ba mảng hoạt động chính"}
            </p>
          </div>
          <div className="space-y-24">
            {servicesItems.length > 0 ? servicesItems.map((svc, idx) => (
              <div key={idx} className="grid lg:grid-cols-2 gap-12 items-center">
                <div className={`${idx === 1 ? 'lg:order-2' : ''} transition-all duration-1000 ${visibleSections.services ? "opacity-100 translate-x-0" : idx === 1 ? "opacity-0 translate-x-12" : "opacity-0 -translate-x-12"}`}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={svc.image || serviceImages[idx] || chuyenDoiSoImage}
                      alt={svc.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/80 to-transparent"></div>
                    <div className="absolute bottom-6 left-6">
                      <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-xl shadow-lg">
                        <i className={`${svc.icon || "ri-building-line"} text-3xl text-white`}></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${idx === 1 ? 'lg:order-1' : ''} transition-all duration-1000 delay-200 ${visibleSections.services ? "opacity-100 translate-x-0" : idx === 1 ? "opacity-0 -translate-x-12" : "opacity-0 translate-x-12"}`}>
                  <h3 className="text-3xl font-bold text-[#2a4b7c] mb-4">{svc.title}</h3>
                  <div className="text-[#4b5563] leading-relaxed prose prose-p:text-[#4b5563] prose-p:mb-4 max-w-none" dangerouslySetInnerHTML={{ __html: svc.description || "" }} />
                </div>
              </div>
            )) : (
              <>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className={`transition-all duration-1000 ${visibleSections.services ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                      <img src={chuyenDoiSoImage} alt="Chuyển đổi số" className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/80 to-transparent"></div>
                      <div className="absolute bottom-6 left-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-xl shadow-lg">
                          <i className="ri-building-line text-3xl text-white"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`transition-all duration-1000 delay-200 ${visibleSections.services ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
                    <h3 className="text-3xl font-bold text-[#2a4b7c] mb-4">Chuyển Đổi Số Doanh Nghiệp</h3>
                    <p className="text-[#4b5563] leading-relaxed mb-4">Xây dựng giải pháp chuyển đổi số cho doanh nghiệp và tổ chức chính phủ. Đây là những sản phẩm lâu dài, tập trung vào việc gia công phần mềm tùy chỉnh, giúp khách hàng tối ưu hóa quy trình kinh doanh, nâng cao hiệu quả hoạt động và thích ứng với môi trường số hóa.</p>
                    <p className="text-[#4b5563] leading-relaxed">Từ hệ thống quản lý dữ liệu đến nền tảng tích hợp AI, chúng tôi cam kết mang đến các giải pháp bền vững, phù hợp với nhu cầu cụ thể của từng đối tác.</p>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className={`lg:order-2 transition-all duration-1000 ${visibleSections.services ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                      <img src={congNgheTienPhongImage} alt="Công nghệ tiên phong" className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/80 to-transparent"></div>
                      <div className="absolute bottom-6 left-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-xl shadow-lg">
                          <i className="ri-brain-line text-3xl text-white"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`lg:order-1 transition-all duration-1000 delay-200 ${visibleSections.services ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
                    <h3 className="text-3xl font-bold text-[#2a4b7c] mb-4">Công Nghệ Tiên Phong</h3>
                    <p className="text-[#4b5563] leading-relaxed mb-4">Phát triển giải pháp công nghệ mới theo xu hướng. Chúng tôi luôn cập nhật và tiên phong trong việc áp dụng các công nghệ mới nhất như AI, blockchain, cloud computing và IoT.</p>
                    <p className="text-[#4b5563] leading-relaxed">Những dự án này không chỉ giải quyết vấn đề hiện tại mà còn dự đoán và dẫn dắt xu hướng tương lai, giúp doanh nghiệp luôn dẫn đầu trong cạnh tranh.</p>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className={`transition-all duration-1000 ${visibleSections.services ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                      <img src={stackwayAcademyImage} alt="Stackway Academy" className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2a4b7c]/80 to-transparent"></div>
                      <div className="absolute bottom-6 left-6">
                        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00c897] to-[#00b587] rounded-xl shadow-lg">
                          <i className="ri-graduation-cap-line text-3xl text-white"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`transition-all duration-1000 delay-200 ${visibleSections.services ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
                    <h3 className="text-3xl font-bold text-[#2a4b7c] mb-4">Stackway Academy</h3>
                    <p className="text-[#4b5563] leading-relaxed mb-4">Với mong muốn góp phần xây dựng nguồn nhân lực chất lượng cao cho xã hội, Stackway Academy cung cấp các chương trình đào tạo chuyên sâu về lập trình, phát triển phần mềm và các kỹ năng công nghệ mới.</p>
                    <p className="text-[#4b5563] leading-relaxed">Chúng tôi hướng đến việc trao quyền cho thế hệ trẻ, giúp họ trang bị kiến thức thực tế để tham gia vào lực lượng lao động số.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} data-section="cta" className="py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f]">
        <div className={`max-w-5xl mx-auto px-6 text-center transition-all duration-1000 ${visibleSections.cta ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Sẵn Sàng Hợp Tác Cùng Stackway?</h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">Hãy để chúng tôi đồng hành cùng bạn trong hành trình chuyển đổi số</p>
          <Link to="/lien-he" className="inline-flex items-center gap-2 px-10 py-4 bg-[#00c897] text-white text-lg font-semibold rounded-lg hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap hover:scale-105 hover:shadow-xl shadow-lg shadow-[#00c897]/30">
            <i className="ri-phone-line text-xl"></i> Liên Hệ Ngay
          </Link>
        </div>
      </section>
    </div>
  );
}
